// Piezas compartidas por las secciones de Analytics > Reservas: formatos,
// tema de las gráficas (mismos colores que Finance), tarjeta de sección, KPI y
// la lista de barras horizontales con la que se pintan todas las distribuciones
// por tramos.

import { useIsDarkMode } from '@/hooks/useIsDarkMode';
import InfoTooltip from '@/components/ui/InfoTooltip';
import { classNames } from '@/lib/utils';

export const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
export const DIAS_CORTOS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const PALETA_CLARA = ['#0B1D2E', '#3B6EA5', '#6FA8DC', '#A3C2E0', '#D1E0F0', '#8FB4D9', '#4A7BB0', '#243B53'];
const PALETA_OSCURA = ['#D1E0F0', '#6FA8DC', '#3B6EA5', '#8FB4D9', '#A3C2E0', '#4A7BB0', '#5B8AC0', '#2E5076'];

export function fmtMoney(value: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

export function fmtNum(value: number, decimales = 0): string {
  return new Intl.NumberFormat('es-ES', { minimumFractionDigits: decimales, maximumFractionDigits: decimales }).format(value);
}

export function fmtPct(value: number, decimales = 0): string {
  return `${fmtNum(value, decimales)}%`;
}

export function fmtDia(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.substring(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** Nombre del país en español a partir del ISO-2; si el navegador no lo sabe, el código. */
export function nombrePais(codigo: string): string {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' }).of(codigo) || codigo;
  } catch {
    return codigo;
  }
}

export function useTemaGraficas() {
  const isDark = useIsDarkMode();
  return {
    grid: isDark ? '#ffffff14' : '#0B1D2E15',
    axis: isDark ? '#9ca3af' : '#0B1D2E99',
    bar: isDark ? '#D1E0F0' : '#0B1D2E',
    bar2: isDark ? '#6FA8DC' : '#6FA8DC',
    bar3: isDark ? '#3B6EA5' : '#A3C2E0',
    cursor: isDark ? '#ffffff10' : '#0B1D2E08',
    palette: isDark ? PALETA_OSCURA : PALETA_CLARA,
    tooltip: isDark
      ? { backgroundColor: '#0B1D2E', border: '1px solid #ffffff22', borderRadius: 8, color: '#fff' }
      : { borderRadius: 8 },
  };
}

export function Tarjeta({ titulo, info, extra, children, className }: {
  titulo: string;
  info?: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={classNames('card', className)}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider inline-flex items-center gap-1.5">
          {titulo}
          {info && <InfoTooltip text={info} />}
        </h2>
        {extra}
      </div>
      {children}
    </div>
  );
}

export function KPI({ icon: Icon, label, value, sub, info, color }: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  info?: string;
  color: string;
}) {
  return (
    <div className="card">
      <div className="flex items-start justify-between mb-2">
        <div className={classNames('p-2.5 rounded-lg', color)}>
          <Icon size={20} />
        </div>
        {info && <InfoTooltip text={info} />}
      </div>
      <p className="text-xs text-sereno-dark/50 truncate">{label}</p>
      <p className="text-lg font-bold truncate">{value}</p>
      {sub && <p className="text-xs text-sereno-dark/40 truncate">{sub}</p>}
    </div>
  );
}

/** Botonera de dos o más opciones, como el «Total / Limpieza» de Finance. */
export function Conmutador<T extends string>({ opciones, valor, onChange }: {
  opciones: { valor: T; label: string }[];
  valor: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden">
      {opciones.map((o) => (
        <button
          key={o.valor}
          onClick={() => onChange(o.valor)}
          className={classNames(
            'px-2.5 py-1 text-xs font-medium transition-colors',
            valor === o.valor ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface FilaBarra {
  etiqueta: string;
  valor: number;
  /** Texto a la derecha de la barra; por defecto, el valor. */
  texto?: string;
  /** Texto secundario más tenue tras el principal (p. ej. «12% cancel.»). */
  detalle?: string;
}

/**
 * Distribución por tramos como lista de barras horizontales. Es más legible
 * que un histograma cuando las etiquetas son frases («Más de 180 días»).
 */
export function BarrasTramos({ filas, vacio = 'Sin datos' }: { filas: FilaBarra[]; vacio?: string }) {
  const max = Math.max(0, ...filas.map((f) => f.valor));
  if (!filas.length || max === 0) return <p className="text-sm text-sereno-dark/40">{vacio}</p>;
  return (
    <div className="space-y-2">
      {filas.map((f) => (
        <div key={f.etiqueta} className="flex items-center gap-3 text-sm">
          <span className="w-28 shrink-0 text-sereno-dark/70 truncate" title={f.etiqueta}>{f.etiqueta}</span>
          <div className="flex-1 h-5 rounded bg-sereno-blue/20 overflow-hidden">
            <div className="h-full rounded bg-sereno-dark an-active" style={{ width: `${(f.valor / max) * 100}%` }} />
          </div>
          <span className="w-32 shrink-0 text-right tabular-nums">
            <span className="font-semibold">{f.texto ?? fmtNum(f.valor)}</span>
            {f.detalle && <span className="ml-1.5 text-xs text-sereno-dark/40">{f.detalle}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
